import { TipoCliente } from '@acme/core'
import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
import { useAuth } from 'hooks'
import { hasPermission } from 'hooks/useBackendMenu'
import {
    useNavigate,
    useOutletContext,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import {
    Cliente,
    clientes as clientes_api,
    EditarClienteSchema,
} from 'service/api'
import { toast } from 'sonner'
import { EditarClienteForm } from '../forms/EditarCliente'
import FormContato from '../forms/FormContato'
import PlanoDiferenciado from './PlanoDiferenciado'

type OutletContext = {
    clientes?: Cliente[]
}

export default function EditarCliente() {
    const navigate = useNavigate()
    const { clienteId } = useParams()
    const [searchParam] = useSearchParams()
    const obrigatorio = searchParam.get('obrigatorio') === 'true'
    const { clientes } = useOutletContext<OutletContext>()
    const {
        decoded: { categoria },
    } = useAuth()
    const toggle = () => navigate('/administracao/clientes')

    const { mutateAsync, isLoading } = clientes_api.mutations.useUpdate({
        onSuccess: () => {
            toast.success('Cliente atualizado com sucesso!')
            clientes_api.pendentesSegmento.invalidateQueries()
        },
        onError: err => {
            toast.error(err.message)
        },
        invalidateQuery: true,
    })

    const cliente = clientes?.find(c => c.id === +clienteId!)
    if (!cliente) return null

    const isRepresentante = cliente.tipo === TipoCliente.REPRESENTANTE,
        existePlanoDiferenciado = cliente.PlanoDiferenciado

    return (
        <TabbedDialog.Root
            disableFields={!hasPermission(['update', 'Clientes'])}
            open={hasPermission(['read', 'Clientes'])}
            onClose={obrigatorio ? () => navigate('/dashboard') : toggle}
            disableEscapeKeyDown={obrigatorio}
            width="2xl"
            loading={isLoading}
            onSubmit={
                !hasPermission(['update', 'Clientes'])
                    ? undefined
                    : values =>
                          mutateAsync({ id: cliente.id, ...values }).catch(
                              e => {
                                  toast.error(e.message)
                                  throw e
                              },
                          )
            }
            schema={EditarClienteSchema}
            defaultValues={{
                ...cliente,
                PlanoDiferenciado: {
                    ...cliente.PlanoDiferenciado,
                    editar: !existePlanoDiferenciado,
                },
                existePlanoDiferenciado,
                emailResponsavel: (cliente as any).email,
            }}
            tabs={[
                {
                    name: 'dados',
                    label: 'Dados do cliente',
                    content: (
                        <EditarClienteForm
                            faixaAtual={cliente.limiteFuncionarios ?? 0}
                        />
                    ),
                },
                ...(isRepresentante
                    ? [
                          {
                              name: 'contato',
                              label: 'Contato',
                              content: <FormContato placeholders={cliente} />,
                          },
                      ]
                    : [
                          {
                              name: 'plano',
                              label: 'Plano diferenciado',
                              content: <PlanoDiferenciado />,
                          },
                      ]),
            ]}
        >
            <TabbedDialog.Title
                action={`Editar ${isRepresentante ? 'representante' : 'cliente'}`}
            >
                {cliente.nomeFantasia}
            </TabbedDialog.Title>
            <TabbedDialog.Content />
            <TabbedDialog.Actions>
                {!obrigatorio && <TabbedDialog.Actions.SecondaryButton />}
                <TabbedDialog.Actions.PrimaryButton closeOnSubmit />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}
